import { useEffect, useRef, useState } from 'react';

/**
 * useScrollAnimation Hook
 * Provides an IntersectionObserver-based trigger for scroll animations.
 * When the observed element enters viewport at given threshold, `isVisible` becomes true.
 * 
 * @param {Object} options - IntersectionObserver options
 * @param {number} options.threshold - Viewport intersection ratio (default: 0.15)
 * @param {string} options.rootMargin - Root margin offset (default: '0px 0px -50px 0px')
 * @param {boolean} options.triggerOnce - Whether animation only runs once (default: true)
 * @returns {[React.RefObject, boolean]} [elementRef, isVisible]
 */
export function useScrollAnimation({
  threshold = 0.15,
  rootMargin = '0px 0px -40px 0px',
  triggerOnce = true,
} = {}) {
  const elementRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const target = elementRef.current;
    if (!target) return;

    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (triggerOnce) {
              observer.unobserve(entry.target);
            }
          } else if (!triggerOnce) {
            setIsVisible(false);
          }
        });
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(target);

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return [elementRef, isVisible];
}

export default useScrollAnimation;
